import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Flex,
  FormLabel,
  Box,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Formik, Form, getIn, setIn } from 'formik';
import _ from 'lodash';
import React from 'react';
import { v4 as uuidv4 } from 'uuid';

import { utils as commonUtils } from '@finley-technologies/common';
import { createBooleanPropertyShell } from '@finley-technologies/credit-agreement-utils';

import { CreditAgreementFullArtifactWipConfigType } from '../../types';
import {
  CheckboxFormField,
  InputFormField,
  TextareaFormField,
} from 'packages/common-frontend/components/FormField';
import {
  CreditAgreement,
  formatBasePropertyKey,
} from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';

export const createUserEntityShell = (): CreditAgreement.DomainEntity.Items.UserEntity => ({
  entity: {
    id: uuidv4(),
    firstName: 'firstName',
    lastName: 'lastName',
    email: 'email@email.com',
  },
  primaryContact: createBooleanPropertyShell('primaryContact'),
  _isWrittenIn_: true,
});

type EditPropertyContainerModalProps = {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  wipConfig: CreditAgreementFullArtifactWipConfigType;
  fullPropertyKey: string;
  children?: (setFieldValue: Function, localValues: any) => React.ReactElement;
};
export const EditPropertyContainerModal = ({
  children,
  isOpen,
  setIsOpen,
  wipConfig: { formProps, saveCreditAgreementFullArtifactWip },
  fullPropertyKey,
}: EditPropertyContainerModalProps) => {
  const currentPropertyContainer = getIn(formProps.values, fullPropertyKey);
  const title = `${formatBasePropertyKey(
    fullPropertyKey,
  )}${` - ${commonUtils.toTitleCase(
    currentPropertyContainer?.deliverableType || '',
  )}`}${` - ${commonUtils.toTitleCase(
    currentPropertyContainer?.frequency || '',
  )}`}`;

  return (
    <Modal isOpen={isOpen} onClose={() => setIsOpen(false)}>
      <ModalOverlay />
      <ModalContent padding="20px" minW="3xl" width="2xl">
        <ModalHeader>Edit Property Container</ModalHeader>
        <ModalCloseButton />
        <ModalBody width="100%">
          <Flex flexDirection="row" alignItems="flex-start" marginTop={5}>
            <FormLabel w="60" mb="0">
              Property
            </FormLabel>
            <Box width="100%" mb="5">
              <Text width="sm">{title}</Text>
            </Box>
          </Flex>
          <Formik
            onSubmit={(props) => {
              // set to change setFieldValue key
              formProps.setFieldValue(fullPropertyKey, {
                ...props,
              });
              const updatedWipCreditAgreement = setIn(
                formProps.values,
                fullPropertyKey,
                props,
              );
              saveCreditAgreementFullArtifactWip({
                variables: {
                  creditAgreementFullArtifactWip: {
                    id: formProps.values.id,
                    creditFacilityId: formProps.values.creditFacilityId,
                    creditAgreement: JSON.stringify(updatedWipCreditAgreement),
                  },
                },
              });
              setIsOpen(false);
            }}
            initialValues={{
              ...currentPropertyContainer,
            }}
          >
            {(localFormProps) => (
              <Form onSubmit={localFormProps.handleSubmit}>
                <VStack spacing={5}>
                  <InputFormField
                    propName="name"
                    label="Name"
                    formProps={localFormProps}
                    value={localFormProps.values.name}
                    labelWidth="60"
                    inputWidth="sm"
                    flexDirection="row"
                    alignItems="flex-end"
                  />
                  <InputFormField
                    propName="section"
                    label="Section"
                    formProps={localFormProps}
                    value={localFormProps.values.section}
                    labelWidth="60"
                    inputWidth="sm"
                    flexDirection="row"
                    alignItems="flex-end"
                  />
                  <TextareaFormField
                    propName="description"
                    label="Description"
                    formProps={localFormProps}
                    value={localFormProps.values.description}
                    labelWidth="60"
                    inputWidth="sm"
                    flexDirection="row"
                    alignItems="flex-end"
                    inputHeight="30"
                  />
                  {children &&
                    children(
                      localFormProps.setFieldValue,
                      localFormProps.values,
                    )}
                  <CheckboxFormField
                    propName="_isWrittenIn_"
                    label="Is Written In"
                    formProps={localFormProps}
                    value={localFormProps.values._isWrittenIn_}
                    labelWidth="60"
                    inputWidth="sm"
                    flexDirection="row"
                  />
                  <TextareaFormField
                    propName="notes"
                    label="Notes"
                    formProps={localFormProps}
                    value={localFormProps.values.notes}
                    labelWidth="60"
                    inputWidth="sm"
                    flexDirection="row"
                    alignItems="flex-end"
                    inputHeight="30"
                  />
                </VStack>
                <Flex justifyContent="center" mt="8">
                  <Button
                    type="submit"
                    aria-label="Done"
                    isDisabled={_.keys(formProps.errors).length > 0}
                  >
                    Done
                  </Button>
                </Flex>
              </Form>
            )}
          </Formik>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
